// i18n bootstrap for the BID frontend.
//
// Wires i18next with the HTTP backend (catalogs served from
// /locales/{lng}/{ns}.json under public/), the browser language detector,
// and react-i18next. The interesting part for 7HAI-176 is the DEV-only
// missing-key reporting: in development every lookup that resolves to no
// translation is logged to the console and pushed onto an in-memory list
// (globalThis.__I18N_MISSING__) so the coverage audit can enumerate gaps.
// None of this is enabled in a production build.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { attachDocumentLanguage } from "./i18nDocumentLang";

// A language belongs here only once its catalog is COMPLETE (7HAI-1121). A
// half-translated language in the picker reads as a broken product rather than
// as work in progress, so `public/locales/<lng>/` may hold a language this list
// does not yet offer — `npm run i18n:coverage` is what tracks those.
// 7HAI-1450: Chinese (simplified), Japanese, Korean and Vietnamese. All four
// have a single CLDR plural category, so their catalogs carry only `_other`.
export const SUPPORTED_LANGUAGES = [
  "en",
  "fi",
  "sv",
  "de",
  "nl",
  "pl",
  "fr",
  "zh",
  "ja",
  "ko",
  "vi",
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isDev = import.meta.env.DEV;

// In-memory sink for missing keys, only populated in DEV. Exposed on
// globalThis so it can be inspected from the console (or scraped by an e2e
// harness).
export interface MissingKeyRecord {
  lng: string;
  ns: string;
  key: string;
  fallback: string;
}

export const missingKeys: MissingKeyRecord[] = [];

if (isDev) {
  (globalThis as unknown as { __I18N_MISSING__?: MissingKeyRecord[] }).__I18N_MISSING__ =
    missingKeys;
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    ns: ["translation"],
    defaultNS: "translation",
    load: "languageOnly",
    interpolation: {
      escapeValue: false, // React already escapes
    },
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
    },
    // DEV-only missing-key reporting. saveMissing makes i18next call
    // missingKeyHandler for any lookup with no translation; we never POST
    // these anywhere — saveMissingTo is "fallback" and the handler only
    // records locally. Guarded so production never pays for this.
    saveMissing: isDev,
    saveMissingTo: "fallback",
    missingKeyHandler: isDev
      ? (lngs, ns, key, fallbackValue) => {
          const lng = Array.isArray(lngs) ? (lngs[0] ?? "?") : String(lngs);
          missingKeys.push({ lng, ns, key, fallback: fallbackValue });

          console.warn(
            `[i18n] missing key "${key}" (ns="${ns}", lng="${lng}") — fallback: "${fallbackValue}"`,
          );
        }
      : undefined,
  });

// <html lang> is hard-coded "en" in index.html and nothing updated it, so a
// Finnish user got a document claiming to be English — which decides screen
// reader pronunciation and whether `hyphens: auto` may break Finnish
// compounds. See i18nDocumentLang.ts; note `detection.order` above already
// reads "htmlTag", so the app consumed an attribute it never maintained.
attachDocumentLanguage(i18n);

export default i18n;
